import { ZERO_WEI } from '@kwenta/sdk/constants'
import { PerpsProvider } from '@kwenta/sdk/types'
import { truncateAddress } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'

import AlertIcon from 'assets/svg/app/alert-triangle.svg'
import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import { Body } from 'components/Text'
import { FlexDivRow } from 'components/layout/flex'
import { EVENT_NAME } from 'constants/matomoEvents'
import { PROVIDER_MAINNET_CHAINS } from 'constants/network'
import Connector from 'containers/Connector'
import { useAnalytics } from 'hooks/useAnalytics'
import useENS from 'hooks/useENS'
import { useMarketPageRoute } from 'hooks/useMarketPageRoute'
import isEqual from 'lodash/isEqual'
import { useRouter } from 'next/router'
import { type FC, useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { DrawerTab, WithdrawTab } from 'sections/accounts/constants'
import { setOpenModal, setOpenModalWithParams } from 'state/app/reducer'
import { selectLeverageSide, selectPerpsProvider } from 'state/futures/common/selectors'
import { selectIsCreatingIsolatedMarginAccount } from 'state/futures/isolatedMargin/selectors'
import { setTradePanelDrawerOpen } from 'state/futures/reducer'
import {
	selectAvailableMargin,
	selectBaseAccount,
	selectIsFetchingTradePreview,
	selectIsIsolatedMarginProvider,
	selectPlaceOrderDisabledReason,
	selectPlaceOrderTranslationKey,
	selectProviderAccount,
	selectWalletBalanceByProvider,
} from 'state/futures/selectors'
import { selectIsOnboardingCrossMargin } from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import {
	selectAbstractionWalletEnabled,
	selectIsWatcherMode,
	selectWallet,
} from 'state/wallet/selectors'
import styled from 'styled-components'

const SubmitTrade: FC = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const router = useRouter()
	const { trackAccountCreation, trackLogin, trackTransferDepositFlow } = useAnalytics()
	const { isWalletConnected, login, switchToChain } = Connector.useContainer()
	const marketPageRoute = useMarketPageRoute()
	const leverageSide = useAppSelector(selectLeverageSide)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const placeOrderTranslationKey = useAppSelector(selectPlaceOrderTranslationKey)
	const fetchingPreview = useAppSelector(selectIsFetchingTradePreview)
	const isCreatingAccount = useAppSelector(selectIsCreatingIsolatedMarginAccount)
	const isOnboardingCrossMargin = useAppSelector(selectIsOnboardingCrossMargin)
	const isWatcherMode = useAppSelector(selectIsWatcherMode)
	const walletAddress = useAppSelector(selectWallet)
	const { ensName } = useENS((walletAddress || '') as string)
	const selectedProviderAccount = useAppSelector(selectProviderAccount)
	const selectedBaseAccount = useAppSelector(selectBaseAccount)
	const placeOrderDisabledReason = useAppSelector(selectPlaceOrderDisabledReason, isEqual)
	const isIsolatedMargin = useAppSelector(selectIsIsolatedMarginProvider)
	const walletBalancesByProvider = useAppSelector(selectWalletBalanceByProvider, isEqual)
	const availableMargin = useAppSelector(selectAvailableMargin)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)

	const balance = useMemo(() => {
		return walletBalancesByProvider.reduce((acc, curr) => {
			return acc.add(wei(curr.totalBalance))
		}, ZERO_WEI)
	}, [walletBalancesByProvider])

	const switchToBase = useCallback(async () => {
		await switchToChain(PROVIDER_MAINNET_CHAINS[PerpsProvider.SNX_V3_BASE])
		router.push(marketPageRoute(PerpsProvider.SNX_V3_BASE))
	}, [router, switchToChain, marketPageRoute])

	const handleCreateAccount = useCallback(async () => {
		if (perpsProvider !== PerpsProvider.SNX_V3_BASE) {
			await switchToBase()
		}
		trackAccountCreation(EVENT_NAME.CLICK_CREATE_ACCOUNT)
		dispatch(setOpenModal('acknowledgement_terms'))
	}, [perpsProvider, dispatch, switchToBase, trackAccountCreation])

	const onSubmit = useCallback(() => {
		dispatch(setTradePanelDrawerOpen(false))
		if (isIsolatedMargin) {
			dispatch(setOpenModal('futures_confirm_isolated_margin_trade'))
		} else {
			dispatch(setOpenModal('futures_confirm_cross_margin_trade'))
		}
	}, [dispatch, isIsolatedMargin])

	const otherReason = useMemo(() => {
		if (!isWalletConnected) {
			return {
				key: 'futures.market.trade.button.connect-wallet',
				action: () => {
					trackLogin(EVENT_NAME.CLICK_LOGIN)
					login?.()
				},
			}
		} else if (!selectedProviderAccount) {
			if (perpsProvider === PerpsProvider.SNX_V3_BASE) {
				return {
					key: 'futures.market.trade.button.create-account',
					action: handleCreateAccount,
				}
			}
			// check whether user has account on base network
			return {
				key: !selectedBaseAccount
					? 'futures.market.trade.button.create-account-on-base'
					: 'futures.market.trade.button.switch-to-base',
				action: !selectedBaseAccount ? handleCreateAccount : switchToBase,
				alertMessage: (
					<MessageWithIconContainer columnGap="8px">
						<AlertIcon width={36} height={16} />
						<Body color="primary">
							<Trans
								i18nKey="account.create-account-intro.deprecated-copy"
								components={[<Emphasis key="provider" />]}
							/>
						</Body>
					</MessageWithIconContainer>
				),
			}
		} else if (abstractionWalletEnabled) {
			if (wei(availableMargin).eq(ZERO_WEI) && balance.eq(ZERO_WEI)) {
				return {
					key: 'futures.market.trade.button.deposit',
					action: () => {
						trackTransferDepositFlow(EVENT_NAME.CLICK_TRANSFER_DEPOSIT)
						dispatch(
							setOpenModalWithParams({
								type: 'manage_wallet',
								params: { tab: DrawerTab.DEPOSIT },
							})
						)
					},
				}
			} else if (wei(availableMargin).eq(ZERO_WEI)) {
				return {
					key: 'futures.market.trade.button.transfer',
					action: () => {
						trackTransferDepositFlow(EVENT_NAME.CLICK_TRANSFER_DEPOSIT)
						dispatch(
							setOpenModalWithParams({
								type: 'manage_wallet',
								params: { tab: DrawerTab.WITHDRAW, innerTab: WithdrawTab.INTERNAL },
							})
						)
					},
				}
			}
		}
		return undefined
	}, [
		isWalletConnected,
		selectedProviderAccount,
		perpsProvider,
		selectedBaseAccount,
		availableMargin,
		balance,
		abstractionWalletEnabled,
		switchToBase,
		trackLogin,
		login,
		dispatch,
		handleCreateAccount,
		trackTransferDepositFlow,
	])

	return (
		<>
			{isWatcherMode ? (
				<ErrorView
					message={t('futures.market.trade.button.watching-address', {
						address: ensName || truncateAddress((walletAddress || '') as string),
					})}
					messageType="warn"
				/>
			) : (
				<div>
					<ManagePositionContainer>
						{otherReason?.alertMessage ? (
							<ErrorView
								message=""
								messageWithIcon={otherReason.alertMessage}
								messageType="warn"
								containerStyle={{ margin: '0' }}
							/>
						) : null}
						<PlaceOrderButton
							data-testid="trade-panel-submit-button"
							noOutline
							fullWidth
							loading={fetchingPreview || isCreatingAccount || isOnboardingCrossMargin}
							variant={otherReason ? 'solid-highlight' : leverageSide}
							disabled={!otherReason && !!placeOrderDisabledReason}
							onClick={otherReason?.action ?? onSubmit}
							size="medium"
						>
							{t(otherReason?.key ?? placeOrderTranslationKey)}
						</PlaceOrderButton>
					</ManagePositionContainer>
				</div>
			)}

			{!otherReason && placeOrderDisabledReason?.show ? (
				<ErrorView
					message={t(placeOrderDisabledReason.message)}
					messageType={placeOrderDisabledReason.show}
					containerStyle={{ margin: '0' }}
				/>
			) : null}
		</>
	)
}

const ManagePositionContainer = styled.div`
	display: flex;
	flex-direction: column;
	grid-gap: 15px;
`

const PlaceOrderButton = styled(TransactionButton)`
	height: 44px;
	text-align: center;
	white-space: normal;
`
const Emphasis = styled.span`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
	font-weight: 700;
`

const MessageWithIconContainer = styled(FlexDivRow)`
	text-align: left;
`

export default SubmitTrade
